import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { BadgeType, PopupPlacement } from '@/dap-design-system'
import { DdsElement } from '@/internal/dds-hu-element'

import styles from './notification-badge.scss?inline'

type BadgeVariant = 'round' | 'dot'

/**
 * `dap-ds-notification-badge`
 * @summary A notification badge component is a user interface element that visually indicates the presence of new information or updates, often displaying a numeric count of unread notifications, messages, or items requiring attention.
 * @element dap-ds-notification-badge
 * @title - Notification badge
 *
 * @slot - The content of the notification badge.
 *
 * @csspart base - The main container of the notification badge.
 * @csspart noty - The notification badge container.
 *
 * @cssproperty --dds-notification-badge-size - Controls the overall size of the notification badge
 * @cssproperty --dds-notification-badge-min-width - Minimum width of the notification badge
 * @cssproperty --dds-notification-badge-height - Height of the notification badge
 * @cssproperty --dds-notification-badge-padding - Padding inside the notification badge
 * @cssproperty --dds-notification-badge-border-radius - Border radius of the notification badge
 * @cssproperty --dds-notification-badge-font-size - Font size of the notification badge text
 * @cssproperty --dds-notification-badge-dot-size - Size of the dot variant
 * @cssproperty --dds-notification-badge-color - Text color of the notification badge
 * @cssproperty --dds-notification-badge-neutral-bg - Background color for neutral type
 * @cssproperty --dds-notification-badge-brand-bg - Background color for brand type
 * @cssproperty --dds-notification-badge-info-bg - Background color for info type
 * @cssproperty --dds-notification-badge-positive-bg - Background color for positive type
 * @cssproperty --dds-notification-badge-warning-bg - Background color for warning type
 * @cssproperty --dds-notification-badge-negative-bg - Background color for negative type
 * @cssproperty --dds-notification-badge-circular-offset-x - Horizontal offset for circular positioning
 * @cssproperty --dds-notification-badge-circular-offset-y - Vertical offset for circular positioning
 */
@customElement('dap-ds-notification-badge')
export default class DapDSNotificationBadge extends DdsElement {
  /** The content of the badge, it can be a number or a string. Content tried to be parsed as a number, if it's not a number, it will be displayed as a string. */
  @property({ type: String }) badgeContent: string

  /** This switch decides the visibility of the badge. This property overrides the badge content visibility. Use it for explicit control. */
  @property({ type: Boolean }) visible: boolean = false

  /** This switch decides whether to show a zero value or not. */
  @property({ type: Boolean }) showZero: boolean = false

  /** The color scheme of the badge
   * @type { 'neutral' | 'brand' | 'info' | 'positive' | 'warning' | 'negative' }
   */
  @property() type: BadgeType = 'negative'

  /** The variant of the badge.
   * @type { 'round' | 'dot' }
   */
  @property() variant: BadgeVariant = 'round'

  /** The cap value of badge content, if the badge content is greater than the max value, it will be displayed as `[number]+`. Zero means no cap. */
  @property({ type: Number }) max: number = 0

  /** The position of the badge content around the slot content.
   * @type { 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' }
   */
  @property({ reflect: true }) placement: PopupPlacement = 'top-end'

  /** Whether to automatically detect circular elements and adjust positioning accordingly */
  @property({ type: Boolean, attribute: 'circular-auto' })
  circularAuto: boolean = true

  /** Override circular positioning detection - forces circular positioning calculation */
  @property({ type: Boolean, attribute: 'force-circular' })
  forceCircular: boolean = false

  @state() private isCircularElement: boolean = false
  @state() private circularOffset: { x: number; y: number } = { x: 0, y: 0 }

  static readonly styles = unsafeCSS(styles)

  connectedCallback() {
    super.connectedCallback()
    this.updateComplete.then(() => {
      this.detectCircularElement()
    })
  }

  private detectCircularElement(): void {
    if (!this.circularAuto && !this.forceCircular) {
      return
    }

    if (this.forceCircular) {
      this.isCircularElement = true
      this.calculateCircularOffset()
      return
    }

    const slotElement = this.shadowRoot?.querySelector('slot')
    const slottedElements = slotElement?.assignedElements()

    if (!slottedElements?.length) {
      return
    }

    const element = slottedElements[0] as HTMLElement

    // Check if element is likely circular based on various indicators
    const isCircular = this.isElementCircular(element)

    if (isCircular !== this.isCircularElement) {
      this.isCircularElement = isCircular
      if (isCircular) {
        this.calculateCircularOffset()
      }
    }
  }

  private isElementCircular(element: HTMLElement): boolean {
    // Check for avatar component
    if (element.tagName.toLowerCase().includes('avatar')) {
      return true
    }

    // Check computed styles for circular indicators
    const computedStyle = getComputedStyle(element)
    const borderRadius = computedStyle.borderRadius

    // If border-radius is 50% or close to half the width/height, it's likely circular
    if (borderRadius === '50%' || borderRadius.includes('50%')) {
      return true
    }

    // Check if border-radius value is close to half the element size
    const rect = element.getBoundingClientRect()
    const minDimension = Math.min(rect.width, rect.height)
    const radiusValue = parseInt(borderRadius, 10)

    if (radiusValue >= minDimension * 0.4) {
      return true
    }

    // Check CSS custom properties that might indicate circular shape
    const customProps = Array.from(document.styleSheets)
      .flatMap(sheet => {
        try {
          return Array.from(sheet.cssRules)
        } catch {
          return []
        }
      })
      .filter(rule => rule instanceof CSSStyleRule)
      .some(rule => {
        const selectorText = (rule as CSSStyleRule).selectorText
        return (
          selectorText?.includes('circle') || selectorText?.includes('rounded')
        )
      })

    return customProps
  }

  private calculateCircularOffset(): void {
    const slotElement = this.shadowRoot?.querySelector('slot')
    const slottedElements = slotElement?.assignedElements()

    if (!slottedElements?.length) {
      return
    }

    const element = slottedElements[0] as HTMLElement
    const rect = element.getBoundingClientRect()
    const radius = Math.min(rect.width, rect.height) / 2

    // Calculate offset based on placement for circular positioning
    // This positions the badge on the circular border rather than the rectangular corner
    const angle = this.getAngleForPlacement(this.placement)
    const offsetDistance = radius * 0.65 // Slightly inside the border for better visual alignment

    this.circularOffset = {
      x: Math.cos(angle) * offsetDistance,
      y: Math.sin(angle) * offsetDistance,
    }

    // Update CSS custom properties for positioning
    this.style.setProperty(
      '--dds-notification-badge-circular-offset-x',
      `${this.circularOffset.x}px`,
    )
    this.style.setProperty(
      '--dds-notification-badge-circular-offset-y',
      `${this.circularOffset.y}px`,
    )
  }

  private getAngleForPlacement(placement: PopupPlacement): number {
    // Convert placement to angle in radians for circular positioning
    switch (placement) {
      case 'top-end':
        return -Math.PI / 4 // -45 degrees (top-right)
      case 'top-start':
        return (-3 * Math.PI) / 4 // -135 degrees (top-left)
      case 'bottom-end':
        return Math.PI / 4 // 45 degrees (bottom-right)
      case 'bottom-start':
        return (3 * Math.PI) / 4 // 135 degrees (bottom-left)
      default:
        return -Math.PI / 4 // Default to top-end
    }
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties)

    if (
      changedProperties.has('placement') ||
      changedProperties.has('forceCircular') ||
      changedProperties.has('circularAuto')
    ) {
      this.detectCircularElement()
    }
  }

  private parseBadgeContent(str: string): string | number {
    if (!str?.trim()) {
      return ''
    }
    const parsed = parseInt(str, 10)
    if (isNaN(parsed)) {
      return str
    }
    return parsed
  }

  private handleMax(value: number, max: number): string {
    return value > max ? `${max}+` : value.toString()
  }

  private renderContent(): string {
    const parsedContent = this.parseBadgeContent(this.badgeContent)
    if (parsedContent === 0 && !this.showZero) {
      return ''
    }
    if (this.max > 0 && typeof parsedContent === 'number') {
      return this.handleMax(parsedContent, this.max).toString()
    }
    return parsedContent.toString()
  }

  private get hide(): boolean {
    return !this.visible && !!this.renderContent() === false
  }

  render() {
    return html`<div part="base" class=${clsx('notification-badge')}>
      <slot></slot>
      <div
        part="noty"
        class=${clsx(
          'notification-badge__info',
          `notification-badge__info--${this.type}`,
          `notification-badge__info--${this.placement}`,
          `notification-badge__info--${this.variant}`,
          {
            'notification-badge__info--hidden': this.hide,
            'notification-badge__info--circular': this.isCircularElement,
          },
        )}>
        ${this.renderContent()}
      </div>
    </div>`
  }
}
